import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Icon } from "semantic-ui-react";

import { statusFilters } from "components";
import ENV from "config/Env";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ROLES } from "models";
import "modules/AccountSettings/AccountSettings.scss";
import {
  ChangePasswordModal,
  LocationSettingsSection,
  MyAccountSection,
  NotificationsSection,
  OrderStatusSection,
  SettingsSection,
  StarStatusSection,
  WorkshopDayOverviewSection
} from "modules/AccountSettings/components";
import { useUpdateUserAccountMutation } from "modules/AccountSettings/hooks";
import { setSelectedIdentifiersToFilter } from "modules/Appointments/hooks";
import { DAYPLANNER_COLUMNS } from "modules/Dayplanner";
import { DAYPLANNER_STORAGE_KEYS } from "modules/Dayplanner/storageKeys";
import { ApiInstance } from "utils/Api";
import { setPreference } from "utils/common";

import type { DropdownItemProps, StatusFilter } from "components";
import type { User } from "models";
import type { DayplannerStatusDataType } from "modules/AccountSettings/components";

type ScrollToProps = {
  scrollTo?: string;
};

const FormHelper = createFormHelper<User>();

export const AccountSettings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<ScrollToProps | undefined>();

  const { user } = usePermanentRealTimeUser();
  const userAccountMutation = useUpdateUserAccountMutation();
  const defaultDayplannerOptions = user?.dayplanner_appointment_status_identifiers ?? [];

  const [isChangePasswordVisible, setIsChangePasswordVisible] = useState<boolean>(false);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [appointmentStatusIdentifiersSetting, setAppointmentStatusIdentifiersSetting] = useState(statusFilters);
  const [dayplannerStatusesFilter, setDayplannerStatusesFilter] = useState<number[]>(defaultDayplannerOptions);

  const setCollapsedColumnsPreferenceValue = () => {
    const columns = [];
    if (user?.day_planner_appointments_collapsed_by_default) columns.push(DAYPLANNER_COLUMNS.Appointments);
    if (user?.day_planner_cars_ready_collapsed_by_default) columns.push(DAYPLANNER_COLUMNS.CarReady);
    if (user?.day_planner_quality_control_collapsed_by_default) columns.push(DAYPLANNER_COLUMNS.QualityCheck);

    setPreference(DAYPLANNER_STORAGE_KEYS.CollapsedColumnsPreference, columns);
  };

  useEffect(() => {
    if (user?.appointment_status_identifiers)
      setAppointmentStatusIdentifiersSetting(
        statusFilters.map(status => ({ ...status, isActive: (user.appointment_status_identifiers ?? []).includes(status.identifier) }))
      );

    setCollapsedColumnsPreferenceValue();
    setSelectedLocations(user?.my_locations ?? []);
    setDayplannerStatusesFilter(defaultDayplannerOptions);
  }, [user]);

  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (!sectionId) return;

    const timeout = setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    return () => clearTimeout(timeout);
  }, [location.state]);

  const handleLocationDropdownChange = (value: number | undefined, options: DropdownItemProps<string>[]) => {
    const selectedOption = options.find(option => option.location_id === value);
    if (!selectedOption || !user) return;

    const location_id = selectedOption.location_id;
    const dealer_id = selectedOption.dealer_id;

    // TODO: this should be a mutation to update the cache
    ApiInstance.post("/users/location/update", { id: user.id, location_id, dealer_id }, ENV.systemBaseURL);
  };

  const onSubmitHandler = (userForm: User) => {
    if (!user) return;

    const userPayload = {
      ...user,
      ...userForm,
      my_locations: selectedLocations,
      appointment_status_identifiers: appointmentStatusIdentifiersSetting.filter(e => e.isActive).map(status => status.identifier),
      dayplanner_appointment_status_identifiers: dayplannerStatusesFilter,
      car_type_filter: userForm.car_type_filter ?? null
    };

    userAccountMutation.mutateAsync(userPayload).then(() => {
      toast.success(t("v8_updated", "Updated"));
      setSelectedIdentifiersToFilter(appointmentStatusIdentifiersSetting);
      if (Notification.permission !== "granted" && userForm.notification_element_ids && userForm.notification_element_ids.length > 0) Notification.requestPermission();
    });
  };

  const handleGoBack = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => history.goBack();
  const handleSelectLocation = (id: number) => setSelectedLocations(curr => (curr.includes(id) ? curr.filter(e => e !== id) : [...curr, id]));
  const handleAppointmentStatusChange = (data: StatusFilter[]) => setAppointmentStatusIdentifiersSetting(data);
  const handleDayplannerStatusChange = (data: DayplannerStatusDataType[]) =>
    setDayplannerStatusesFilter(data.filter(({ isActive }) => isActive).map(({ value }) => value));

  if (!user) return null;

  return (
    <FormHelper.Form onSubmit={onSubmitHandler} defaultValues={user}>
      <div>
        <div className="AccountSetting">
          <MyAccountSection onChangePassword={() => setIsChangePasswordVisible(true)} onProfilePictureChange={() => {}} />
          <SettingsSection
            handleLocationDropdownChange={handleLocationDropdownChange}
            handleAppointmentStatusChange={handleAppointmentStatusChange}
            appointmentStatusIdentifiersSetting={appointmentStatusIdentifiersSetting}
          />
          <WorkshopDayOverviewSection handleDayplannerStatusChange={handleDayplannerStatusChange} dayplannerStatusesFilter={dayplannerStatusesFilter} />
          {[ROLES.CallCenter, ROLES.WarrantyManager].includes(user.role_id) && (
            <LocationSettingsSection handleSelectLocation={handleSelectLocation} selectedLocations={selectedLocations} />
          )}
          <OrderStatusSection />
          <StarStatusSection />
          <NotificationsSection />
        </div>
        <div className="FixedButtonSection">
          <div className="FixedButtonSection-inner">
            <Button type="button" size="medium" basic className="cancel-btn" onClick={handleGoBack}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            <Button type="submit" color="green" className="save-btn">
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </div>
        <ChangePasswordModal isOpen={isChangePasswordVisible} onClose={() => setIsChangePasswordVisible(false)} />
      </div>
    </FormHelper.Form>
  );
};
